/* eslint-disable eqeqeq */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { Dropdown, Table, Button } from "react-bootstrap";
import { useCallback, useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import { useNavigate } from "react-router-dom";
import { PropagateLoader } from "react-spinners";
import { useTranslate } from "../hooks/LanguageProvider";
import { generateExcelFile, generatePDFFile } from "../utilities/functions";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:9000";
const PAGE_ROLE_EXPERT = "4";

type ExpertUser = {
  id_user: number;
  id_role: number;
  role_name: string;
  nom_user: string | null;
  prenom_user: string | null;
  company: string | null;
  email_user: string | null;
  username_user: string | null;
  wilaya: string | null;
  phone_user: string | null;
  date_creation_user: string | null;
};

type ExpertColumnKey =
  | "id_user"
  | "company"
  | "full_name"
  | "email_user"
  | "phone_user"
  | "wilaya"
  | "role_name";

const valueOrDash = (value?: string | number | null) => {
  const text = String(value ?? "").trim();
  return text || "-";
};

const getExpertName = (expert: ExpertUser) =>
  [expert.nom_user, expert.prenom_user]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" ");

export function Experts() {
  const { translate } = useTranslate();
  const navigate = useNavigate();

  const [experts, setExperts] = useState<ExpertUser[]>([]);
  const [pageCount, setPageCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [limitValue, setSelectedLimitValue] = useState(10);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [column, setSortColumn] = useState("id_user");
  const [sort, setSort] = useState("DESC");
  const [search, setSearch] = useState("");
  const [type, setType] = useState(5);
  const [typeSearch, setTypeSearch] = useState("company");
  const [selectedExperts, setSelectedExperts] = useState<Set<number>>(new Set());
  const [selectedColumns, setSelectedColumns] = useState<Record<ExpertColumnKey, boolean>>({
    id_user: true,
    company: true,
    full_name: true,
    email_user: true,
    phone_user: true,
    wilaya: true,
    role_name: true,
  });

  const searchOptions = [
    { key: "id", label: translate("id"), type: 0 },
    { key: "company", label: translate("company"), type: 5 },
    { key: "full_name", label: translate("name"), type: 7 },
    { key: "email", label: translate("email"), type: 2 },
    { key: "phone", label: translate("phone"), type: 3 },
    { key: "wilaya", label: translate("wilaya"), type: 6 },
    { key: "role", label: translate("role"), type: 4 },
  ];

  const expertHeaders = ["ID", "Entreprise", "Nom", "Email", "Telephone", "Wilaya", "Role"];

  const getExportRows = (rows: ExpertUser[]) =>
    rows.map((expert) => [
      expert.id_user,
      valueOrDash(expert.company || expert.username_user),
      valueOrDash(getExpertName(expert)),
      valueOrDash(expert.email_user),
      valueOrDash(expert.phone_user),
      valueOrDash(expert.wilaya),
      valueOrDash(expert.role_name || "Expert"),
    ]);

  const onDownloadConfirm = (format: string) => {
    const rows = getExportRows(experts);

    if (format === "excel") {
      generateExcelFile("Experts List", expertHeaders, rows);
    }

    if (format === "pdf") {
      generatePDFFile("Experts List", expertHeaders, rows);
    }
  };

  const getExperts = useCallback(
    async (
      limit: number,
      page: number,
      nextSearch: string,
      nextType: number,
      sortColumn: string,
      sortOrder: string,
    ) => {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
          sortColumn,
          sortOrder,
          search: nextSearch,
          type: String(nextType),
          roleId: PAGE_ROLE_EXPERT,
        });

        const [listResponse, totalResponse] = await Promise.all([
          fetch(`${backendUrl}/api/users?${params.toString()}`, { mode: "cors" }),
          fetch(`${backendUrl}/api/users/total?${params.toString()}`, { mode: "cors" }),
        ]);

        if (!listResponse.ok || !totalResponse.ok) {
          throw new Error("Error retrieving experts list");
        }

        const listData = await listResponse.json();
        const totalData = await totalResponse.json();
        const nextTotal = Number(totalData?.[0]?.total || 0);

        setExperts(Array.isArray(listData) ? listData : []);
        setTotal(nextTotal);
        setPageCount(Math.ceil(nextTotal / limit));
        return Array.isArray(listData) ? listData : [];
      } catch (error) {
        console.error("Erreur chargement experts:", error);
        setExperts([]);
        setTotal(0);
        setPageCount(0);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    getExperts(limitValue, currentPage, search, type, column, sort);
  }, [column, currentPage, getExperts, limitValue, search, sort, type]);

  useEffect(() => {
    setSelectedExperts(new Set());
  }, [currentPage, experts]);

  const handleAdvancedSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setCurrentPage(1);
  };

  const handleTypeSearch = (option: (typeof searchOptions)[number]) => {
    setType(option.type);
    setTypeSearch(option.key);
    setCurrentPage(1);
  };

  const handleSortingColumn = (currentColumn: string) => {
    const nextSort = column === currentColumn && sort === "DESC" ? "ASC" : "DESC";
    setSort(nextSort);
    setSortColumn(currentColumn);
    setCurrentPage(1);
  };

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLimitValue(Number(event.target.value));
    setCurrentPage(1);
    window.scrollTo(0, 0);
  };

  const handlePageClick = (data: { selected: number }) => {
    setCurrentPage(data.selected + 1);
    window.scrollTo(0, 0);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();

    if (event.target.checked) {
      setSelectedExperts(new Set(experts.map((expert) => expert.id_user).filter(Boolean)));
      return;
    }

    setSelectedExperts(new Set());
  };

  const handleSelectExpert = (event: React.ChangeEvent<HTMLInputElement>, expertId: number) => {
    event.stopPropagation();
    const nextSelected = new Set(selectedExperts);

    if (nextSelected.has(expertId)) {
      nextSelected.delete(expertId);
    } else {
      nextSelected.add(expertId);
    }

    setSelectedExperts(nextSelected);
  };

  const handleColumnChange = (columnKey: ExpertColumnKey) => {
    setSelectedColumns((prev) => ({ ...prev, [columnKey]: !prev[columnKey] }));
  };

  const isAllSelected = experts.length > 0 && selectedExperts.size === experts.length;
  const isSomeSelected = selectedExperts.size > 0 && selectedExperts.size < experts.length;
  const displayedCount = experts.length;

  const actionButtonStyle = {
    width: "38px",
    height: "38px",
    padding: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
  } as const;

  return (
    <div className="container-fluid">
      <div className="row mb-3">
        <div className="col-md-6">
          <h4 className="mb-0">
            <i className="las la-user-tie me-2"></i>
            {translate("Experts")} ({total})
          </h4>
        </div>

        <div className="col-md-6 text-end">
          <Dropdown className="d-inline me-2">
            <Dropdown.Toggle variant="warning">{translate("export")}</Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => onDownloadConfirm("excel")}>Excel</Dropdown.Item>
              <Dropdown.Item onClick={() => onDownloadConfirm("pdf")}>PDF</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <button className="btn btn-warning" onClick={() => navigate("/User?role=expert")}>
            + {translate("new_expert") || "Nouvel expert"}
          </button>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-4">
          <div className="input-group">
            <Dropdown>
              <Dropdown.Toggle variant="link">
                <i className="fas fa-chevron-down"></i>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {searchOptions.map((option) => (
                  <Dropdown.Item key={option.key} onClick={() => handleTypeSearch(option)}>
                    {option.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            <input
              type="text"
              className="form-control"
              placeholder={`${translate("search_by")} ${translate(typeSearch) || typeSearch}`}
              value={search}
              onChange={handleAdvancedSearch}
            />

            <Button
              variant="secondary"
              style={{ height: "38px" }}
              title={translate("clear_search")}
              onClick={() => {
                setSearch("");
                setCurrentPage(1);
              }}
            >
              x
            </Button>
          </div>
        </div>

        <div className="col-md-8 d-flex justify-content-end align-items-center">
          <div className="dataTables_length" id="DataTables_Table_0_length">
            <label style={{ marginBottom: 0 }}>
              {translate("Show")}
              <select
                name="DataTables_Table_0_length"
                aria-controls="DataTables_Table_0"
                className="custom-select custom-select-sm form-control form-control-sm ml-2"
                style={{ width: "66px" }}
                value={limitValue}
                onChange={handleSelectChange}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
              </select>
            </label>
          </div>

          <Dropdown>
            <Dropdown.Toggle variant="" title={translate("Display columns")}>
              <i className="las la-eye"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {([
                ["id_user", translate("id")],
                ["company", translate("company")],
                ["full_name", translate("name")],
                ["email_user", translate("email")],
                ["phone_user", translate("phone")],
                ["wilaya", translate("wilaya")],
                ["role_name", translate("role")],
              ] as [ExpertColumnKey, string][]).map(([key, label]) => (
                <Dropdown.Item as="button" key={key} onClick={(event) => event.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedColumns[key]}
                    onChange={() => handleColumnChange(key)}
                  />
                  <span>{label}</span>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <div className="row">
        <div className="table-responsive">
          <Table className="dataTable table">
            <thead className="bg-white text-uppercase">
              <tr className="ligth ligth-data">
                <th style={{ width: "5%" }}>
                  <div className="checkbox d-inline-block">
                    <input
                      type="checkbox"
                      className="checkbox-input"
                      checked={isAllSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = isSomeSelected;
                      }}
                      onChange={handleSelectAll}
                    />
                  </div>
                </th>

                {selectedColumns.id_user && (
                  <th className="sorting" onClick={() => handleSortingColumn("id_user")}>
                    {translate("id")}
                  </th>
                )}
                {selectedColumns.company && (
                  <th className="sorting" onClick={() => handleSortingColumn("company")}>
                    {translate("company")}
                  </th>
                )}
                {selectedColumns.full_name && <th>{translate("name")}</th>}
                {selectedColumns.email_user && (
                  <th className="sorting" onClick={() => handleSortingColumn("email_user")}>
                    {translate("email")}
                  </th>
                )}
                {selectedColumns.phone_user && (
                  <th className="sorting" onClick={() => handleSortingColumn("phone_user")}>
                    {translate("phone")}
                  </th>
                )}
                {selectedColumns.wilaya && (
                  <th className="sorting" onClick={() => handleSortingColumn("wilaya")}>
                    {translate("wilaya")}
                  </th>
                )}
                {selectedColumns.role_name && <th>{translate("role")}</th>}
                <th>{translate("actions")}</th>
              </tr>
            </thead>

            <tbody className="ligth-body">
              {loading ? (
                <tr>
                  <td className="text-center" colSpan={8}>
                    <PropagateLoader size={15} />
                  </td>
                </tr>
              ) : experts.length > 0 ? (
                experts.map((expert) => {
                  const companyName = valueOrDash(expert.company || expert.username_user);
                  const fullName = valueOrDash(getExpertName(expert));

                  return (
                    <tr key={expert.id_user}>
                      <td>
                        <div className="checkbox d-inline-block">
                          <input
                            type="checkbox"
                            className="checkbox-input"
                            checked={selectedExperts.has(expert.id_user)}
                            onChange={(event) => handleSelectExpert(event, expert.id_user)}
                            onClick={(event) => event.stopPropagation()}
                          />
                        </div>
                      </td>

                      {selectedColumns.id_user && <td>{expert.id_user}</td>}
                      {selectedColumns.company && (
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div
                              style={{
                                width: 42,
                                height: 42,
                                borderRadius: 8,
                                backgroundColor: "#020815",
                                border: "1px solid #020815",
                                color: "#facc15",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                fontWeight: 800,
                              }}
                            >
                              {companyName.slice(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <div className="fw-semibold">{companyName}</div>
                              <div className="small text-muted">{valueOrDash(expert.username_user)}</div>
                            </div>
                          </div>
                        </td>
                      )}
                      {selectedColumns.full_name && <td>{fullName}</td>}
                      {selectedColumns.email_user && <td>{valueOrDash(expert.email_user)}</td>}
                      {selectedColumns.phone_user && <td>{valueOrDash(expert.phone_user)}</td>}
                      {selectedColumns.wilaya && <td>{valueOrDash(expert.wilaya)}</td>}
                      {selectedColumns.role_name && (
                        <td>
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              padding: "4px 10px",
                              borderRadius: "999px",
                              backgroundColor: "#020815",
                              color: "#ffffff",
                              fontSize: 12,
                              fontWeight: 700,
                            }}
                          >
                            {valueOrDash(expert.role_name || "Expert")}
                          </span>
                        </td>
                      )}
                      <td className="text-center">
                        <button
                          className="btn btn-sm me-2 rounded-circle"
                          style={{
                            ...actionButtonStyle,
                            backgroundColor: "#020815",
                            border: "1px solid #020815",
                            color: "#ffffff",
                          }}
                          title={translate("go_to_profile")}
                          onClick={() => navigate(`/expert-profile/${expert.id_user}`)}
                        >
                          <i className="fas fa-user"></i>
                        </button>

                        <button
                          className="btn btn-sm btn-warning rounded-circle"
                          style={actionButtonStyle}
                          title={translate("edit")}
                          onClick={() => navigate(`/User/${expert.id_user}?from=Experts`)}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8}>{translate("no_experts") || "Aucun compte expert trouve."}</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      <div className="d-flex justify-content-between mt-3">
        <span>
          {translate("displaying")} {displayedCount} {translate("out_of")} {total}
        </span>

        <ReactPaginate
          previousLabel={translate("previous")}
          nextLabel={translate("next")}
          breakLabel="..."
          pageCount={pageCount}
          marginPagesDisplayed={2}
          pageRangeDisplayed={3}
          onPageChange={handlePageClick}
          containerClassName="pagination justify-content-end"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="page-item"
          previousLinkClassName="page-link"
          nextClassName="page-item"
          nextLinkClassName="page-link"
          breakClassName="page-item"
          breakLinkClassName="page-link"
          activeClassName="active"
          forcePage={currentPage - 1}
        />
      </div>
    </div>
  );
}
